/*
 * THE FINDER — "describe the card you want".
 *
 * Daniel, 2026-10-05: "a plain text box where someone can write, for example,
 * 'I want to send a birthday card to my dad', and then, based on that, we
 * will find options that are a good fit."
 *
 * WHERE IT LIVES. On the occasion screen, beside the occasions: a customer
 * either taps one or says it in their own words. The answer is shown on the
 * occasion page itself (snippets/design-picker.liquid) -- the same tiles, the
 * same sheet, the same "Add your pictures" -- so nothing after the search is
 * new to learn or new to break.
 *
 * WHAT ANSWERS. artists.picturepops.com/finder.json, the artist platform's
 * Worker (projects/pop-cards/artists-worker/src/finder in the venture repo).
 * It reads the sentence and ranks the published designs from the facts the
 * tagger wrote about each card. It answers with art keys, best first, plus
 * what it understood; this file turns the keys back into the catalogue's own
 * designs. A removed chip ("For Dad x") sends the reading back with that part
 * taken out: ranked again, not read again.
 *
 * FINDER MODE is `finder` being set. Going back to the occasions ends it
 * (configurator.js, the entryStep watcher), and a tapped occasion never sees
 * it. Mixed into the builder component after design-picker.js.
 */
(function () {
  'use strict';

  window.PP = window.PP || {};

  // Delegates to configurator.js's track(), like design-picker.js does.
  function track(name, params) {
    try {
      if (window.PP && window.PP.track) window.PP.track(name, params);
    } catch (e) { /* analytics never blocks a search */ }
  }

  /* ⛔ OFF FOR CUSTOMERS until /finder.json is deployed and the designs are
     tagged: a box that cannot answer is worse than no box. Flip LIVE in one
     commit when both are true. Until then ?finder=1 turns it on for that
     browser tab, to look at it on Draft or Live without anyone else seeing it. */
  const LIVE = false;
  function enabled() {
    if (LIVE) return true;
    const asked = /[?&]finder=1(&|#|$)/.test(window.location.search);
    try {
      if (asked) sessionStorage.setItem('pp_finder_preview', '1');
      return sessionStorage.getItem('pp_finder_preview') === '1';
    } catch (e) { return asked; }
  }

  const FINDER_URL = 'https://artists.picturepops.com/finder.json';
  const TIMEOUT_MS = 8000;
  const MAX_Q = 200;

  // "For ___", on a chip and in the title. The person is the point of the
  // card, so these are the words a customer would use, not the list's.
  const FOR = {
    anyone: 'anyone', mom: 'Mom', dad: 'Dad', parents: 'your parents', grandma: 'Grandma',
    grandpa: 'Grandpa', grandparent: 'a grandparent', partner: 'your partner', wife: 'your wife',
    husband: 'your husband', couple: 'a couple', daughter: 'your daughter', son: 'your son',
    kids: 'a child', baby: 'a baby', teen: 'a teenager', sister: 'your sister',
    brother: 'your brother', family: 'the family', friend: 'a friend',
    'best-friend': 'your best friend', coworker: 'a coworker', boss: 'your boss',
    teacher: 'a teacher', caregiver: 'a caregiver', host: 'a host', neighbor: 'a neighbor',
    'pet-lover': 'an animal lover', 'book-lover': 'a book lover', 'art-lover': 'an art lover',
    traveler: 'a traveler',
  };
  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August',
    'September', 'October', 'November', 'December'];
  const words = function (s) { return String(s || '').replace(/-/g, ' '); };
  const cap = function (s) { s = words(s); return s.charAt(0).toUpperCase() + s.slice(1); };

  window.PP.finderState = function () {
    return {
      finderQ: '',        // the box. Kept when the customer goes back, so they can change a word.
      finderBusy: false,
      finderError: '',
      finder: null,       // the answer on screen: { q, via, read, cards, near } (art keys). Finder mode while set.
      finderEnabled: enabled(),
    };
  };

  window.PP.finderMethods = {
    finderOn() { return !!this.finder; },

    /* One request, never a thrown error: null when the network, the Worker
       or the clock says no. Eight seconds, because a model may be reading. */
    async _finderAsk(params) {
      let u;
      try { u = new URL(window.PP_FINDER_URL || FINDER_URL); } catch (e) { return null; }
      Object.keys(params).forEach(function (k) { u.searchParams.set(k, params[k]); });
      const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
      const timer = setTimeout(function () { if (ctrl) ctrl.abort(); }, TIMEOUT_MS);
      try {
        const r = await fetch(u.toString(), { credentials: 'omit', signal: ctrl ? ctrl.signal : undefined });
        const j = r.ok ? await r.json() : null;
        return j && Array.isArray(j.cards) ? j : null;
      } catch (e) {
        return null;
      } finally {
        clearTimeout(timer);
      }
    },

    _finderTake(j, q) {
      this.finder = {
        q: q, via: j.via, read: j.read || {}, note: typeof j.note === 'string' ? j.note : '',
        cards: j.cards.map(function (c) { return c.id; }),
        near: (j.near || []).map(function (c) { return c.id; }),
      };
      if (typeof this._defaultView === 'function') this._defaultView();
    },

    async finderSearch() {
      const q = String(this.finderQ || '').replace(/\s+/g, ' ').trim().slice(0, MAX_Q);
      if (!q || this.finderBusy) return;
      this.finderBusy = true;
      this.finderError = '';
      const j = await this._finderAsk({ q: q });
      this.finderBusy = false;
      if (!j) {
        this.finderError = 'We couldn’t search just now. Try again, or pick an occasion.';
        return;
      }
      const wasOn = !!this.finder;
      this._finderTake(j, q);
      const read = this.finder.read;
      /* The sentence itself goes too, so the finder can be tuned on what people
         really type (Daniel, 2026-10-05: "keep raw text in posthog"). It can
         hold names and health details; it is capped at 200 characters. */
      track('pp_finder_search', {
        q: q, via: j.via, results: this._finderPick(this.finder.cards).length, near: this._finderPick(this.finder.near).length, length: q.length,
        occasion: (read.occasions || [])[0] || null, recipient: (read.recipients || [])[0] || null,
      });
      this.designSheet = null;
      if (wasOn) {
        // A second search from the results: same screen, so the URL changes in place.
        try {
          const u = new URL(window.location.href);
          u.searchParams.set('find', q);
          window.history.replaceState(window.history.state, '', u.pathname + u.search + u.hash);
        } catch (e) { /* the search still stands */ }
        return;
      }
      this.entryDir = 'fwd';
      this.entryStep = 'design';
    },

    /* What the finder understood, as chips. The words it did not turn into
       anything stay off: they are the customer's own and still count. */
    finderChips() {
      const r = (this.finder && this.finder.read) || {};
      const self = this;
      const out = [];
      (r.occasions || []).forEach(function (h) { out.push({ kind: 'occasions', value: h, label: self._finderOcc(h) }); });
      (r.recipients || []).forEach(function (h) {
        if (h !== 'anyone') out.push({ kind: 'recipients', value: h, label: 'For ' + (FOR[h] || words(h)) });
      });
      ['subjects', 'tones', 'artStyles', 'colors', 'motifs'].forEach(function (k) {
        (r[k] || []).forEach(function (v) { out.push({ kind: k, value: v, label: cap(v) }); });
      });
      if (r.zodiac) out.push({ kind: 'zodiac', value: r.zodiac, label: cap(r.zodiac) });
      if (r.birthMonth) out.push({ kind: 'birthMonth', value: r.birthMonth, label: 'Born in ' + MONTHS[r.birthMonth - 1] });
      if (r.series) out.push({ kind: 'series', value: r.series, label: cap(r.series) });
      if (r.credit) out.push({ kind: 'credit', value: r.credit, label: cap(r.credit) });
      const ex = r.exclude || {};
      (ex.occasions || []).forEach(function (h) { out.push({ kind: 'exclude.occasions', value: h, label: 'Not ' + self._finderOcc(h).toLowerCase() }); });
      (ex.tones || []).forEach(function (v) { out.push({ kind: 'exclude.tones', value: v, label: 'Not ' + words(v) }); });
      (ex.motifs || []).forEach(function (v) { out.push({ kind: 'exclude.motifs', value: v, label: 'No ' + words(v) }); });
      (ex.subjects || []).forEach(function (v) { out.push({ kind: 'exclude.subjects', value: v, label: 'No ' + words(v) }); });
      return out;
    },

    async finderRemove(kind, value) {
      if (!this.finder || this.finderBusy) return;
      const read = JSON.parse(JSON.stringify(this.finder.read || {}));
      const path = kind.split('.');
      const host = path.length === 2 ? (read[path[0]] = read[path[0]] || {}) : read;
      const key = path[path.length - 1];
      if (Array.isArray(host[key])) host[key] = host[key].filter(function (v) { return v !== value; });
      else host[key] = null;
      this.finderBusy = true;
      const j = await this._finderAsk({ read: JSON.stringify(read) });
      this.finderBusy = false;
      if (!j) { this.finderError = 'That didn’t work just now. Try again in a moment.'; return; }
      this.finderError = '';
      this._finderTake(j, this.finder.q);
      track('pp_finder_refined', { removed: kind + ':' + value, results: this._finderPick(this.finder.cards).length });
    },

    /* The designs to show, in the finder's order. Keys the catalogue does not
       hold (it timed out, or a design was unpublished since) are skipped. When
       nothing fits exactly, the near list -- the same occasion for anyone. */
    _finderPick(keys) {
      const by = Object.create(null);
      (this.designs || []).forEach(function (d) { by[d.id] = d; if (d.artKey) by[d.artKey] = d; });
      return (keys || []).map(function (k) { return by[k]; }).filter(Boolean);
    },

    finderShown() {
      if (!this.finder) return [];
      const exact = this._finderPick(this.finder.cards);
      return exact.length ? exact : this._finderPick(this.finder.near);
    },

    finderIsNear() {
      return !!this.finder && !this._finderPick(this.finder.cards).length && this._finderPick(this.finder.near).length > 0;
    },

    // An occasion's name. One the site has no page for yet (a model may read
    // "sympathy") still reads as a word, not a handle.
    _finderOcc(h) {
      const label = this.occasionLabelOf(h);
      return label === h ? cap(h) : label;
    },

    finderTitle() {
      if (!this.finderShown().length) return 'Nothing fits that yet';
      if (this.finderIsNear()) return 'Nothing quite fits yet';
      const r = this.finder.read || {};
      const occ = (r.occasions || [])[0];
      const who = (r.recipients || []).filter(function (h) { return h !== 'anyone'; })[0];
      const forWho = who ? ' for ' + (FOR[who] || words(who)) : '';
      if (occ) return this._finderOcc(occ) + ' cards' + forWho;
      if (who) return 'Cards' + forWho;
      return 'Cards that fit';
    },

    finderSubtitle() {
      const n = this.finderShown().length;
      if (!n) return 'Try other words, or pick an occasion.';
      if (this.finderIsNear()) return 'These come closest.';
      // A gentle search (a loss, an illness, a hard time) with few cards made for it says so
      // honestly; the finder writes the line (artists-worker finder/search.js noteOf, 13 Oct).
      if (this.finder.note) return this.finder.note;
      if (this.narrow) return '';          // the phone toolbar already counts them
      return n === 1 ? '1 design' : n + ' designs';
    },

    /* The occasion a design chosen from the results takes: the one the sentence
       named if this design is on it, else the one the design itself is for
       (design-picker.js designOccasion: made for it, else its cover suits it,
       else Just because for a generic card). A design on no occasion page takes
       whatever the sentence named, or none.
       ⛔ NOT ITS FIRST OCCASION PAGE, AND NEVER ITS STRIPPED GREETING'S (Daniel,
       2026-10-08: an occasion only where the cover, or cover and inside together,
       are for it). `occasions[0]` is only "made for" by the catalogue's ordering. */
    finderOccasionFor(d) {
      if (!d) return null;
      const want = (this.finder && this.finder.read && this.finder.read.occasions) || [];
      const mine = d.occasions || [];
      if (!mine.length) return want[0] || null;
      const own = window.PP.designOccasion ? window.PP.designOccasion(d) : '';
      return want.filter(function (o) { return mine.indexOf(o) !== -1; })[0] || own || null;
    },

    /* Chosen from the results, before any occasion was asked: the occasion
       above dresses the card (applyOccasion, the headline, the inside top's
       words), and the cart sends it as _Occasion -- routing only, never printed. */
    finderBeforeChoose(d) {
      if (!this.finder || !d) return;
      const occ = this.finderOccasionFor(d);
      if (occ && occ !== this.occasion && typeof this.applyOccasion === 'function') {
        this.occPick = occ;
        this.applyOccasion(occ, true);
      }
      /* ⭐ A GENERIC CARD THE SENTENCE NAMED NO OCCASION FOR (2026-10-08): Just because above is only its
         order's occasion (routing). Its inside top starts on the no-occasion words, as it does from All cards
         (Daniel, 8 Oct; configurator.js topWordsOcc, inside-top.js words). Any other pick: the words follow
         the occasion just set. */
      if (typeof this.topWordsFrom === 'function') this.topWordsFrom(this.finderNamedNone(d) ? 'any' : null);
    },
    // True when the occasion a design takes from the results is only the generic card's Just because.
    finderNamedNone(d) {
      const want = (this.finder && this.finder.read && this.finder.read.occasions) || [];
      const mine = d.occasions || [];
      const named = want.filter(function (o) { return mine.indexOf(o) !== -1; })[0];
      const own = (d.made || [])[0] || (d.suits || [])[0];
      return d.any === true && !named && !own;
    },

    // 1-based place of a design in the results, for the analytics.
    finderPosition(id) {
      const i = this.finderShown().map(function (d) { return d.id; }).indexOf(id);
      return i === -1 ? null : i + 1;
    },
  };
})();
